import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, Check } from 'lucide-react';
import { usePane, PANES, type PaneType } from '../../shared/contexts/PaneContext';
import { PANE_ICONS } from '../../shared/pane-icons';
import { useProject } from '../../contexts/ProjectContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

/**
 * Dropdown component for switching between workspace panes.
 */
export const PaneSwitcher = () => {
  const { activePane, paneInfo, setActivePane } = usePane();
  const { shortId } = useProject();
  const { wsSlug: ctxWsSlug } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();
  // Fall back to URL-derived slug when workspace context hasn't loaded yet
  const wsSlug = ctxWsSlug ?? location.pathname.match(/^\/w\/([^/]+)/)?.[1] ?? 'archie';
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePaneSelect = (paneId: PaneType) => {
    setActivePane(paneId);
    const prefix = PANES[paneId].urlPrefix;
    if (wsSlug) {
      if (shortId && paneId === 'product') {
        navigate(`/w/${wsSlug}/${prefix}/${shortId}`);
      } else {
        navigate(`/w/${wsSlug}/${prefix}`);
      }
    } else {
      navigate(PANES[paneId].defaultPath);
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl
          bg-surface-subtle/80 hover:bg-surface-subtle/80
          border border-line/50
          transition-all duration-200 group"
      >
        <div className="flex items-center gap-2.5">
          <span className="text-coral-500">{PANE_ICONS[activePane]}</span>
          <div className="text-left">
            <div className="text-sm font-semibold text-text leading-tight">
              {paneInfo.name}
            </div>
            <div className="text-[10px] text-subtle leading-tight">
              {paneInfo.description}
            </div>
          </div>
        </div>
        <ChevronDown
          size={16}
          className={`text-subtle transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 py-1.5
          bg-white rounded-xl shadow-lg border border-line/80
          z-50 animate-fade-in overflow-hidden"
        >
          {Object.values(PANES).map((pane) => {
            const isActive = pane.id === activePane;
            return (
              <button
                key={pane.id}
                onClick={() => handlePaneSelect(pane.id)}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 text-left
                  transition-colors duration-150
                  ${isActive
                    ? 'bg-coral-50 text-coral-600'
                    : 'text-muted hover:bg-surface-muted'
                  }
                `}
              >
                <span className={isActive ? 'text-coral-500' : 'text-subtle'}>
                  {PANE_ICONS[pane.id]}
                </span>
                <div className="flex-1">
                  <div className={`text-sm font-medium ${isActive ? 'text-coral-600' : 'text-text'}`}>
                    {pane.name}
                  </div>
                  <div className="text-[10px] text-subtle">
                    {pane.description}
                  </div>
                </div>
                {isActive && (
                  <Check size={16} className="text-coral-500" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
